import React from 'react';
import {all, call, fork, put, select, take, takeLatest, takeEvery} from 'bukku/shared/SagaService';
import {message, notification} from 'antd';
import actions from './actions';
import AppActions from '../App/actions';
import {deleto, get, patch, post, puto} from 'bukku/shared/ApiService';
import {getMsgType, getMsgTypePluralized, getOutstandingUrl, getStatus, Type} from './helper';
import {plural, t, Trans} from '@lingui/macro';
import Big from "big.js";
import {initRequest} from "./initTransaction/saga";
import {getServerUrl} from "./helpers/getServerUrl";
import { getWebBaseUrl } from './helpers/getWebBaseUrl';
import {history} from "../store";
import { windowOpenPopUpFallback } from '../../helpers/windowOpenPopUpFallback';

// Create
export function* createRequest() {
    yield takeEvery(actions.TRANSACTION_CREATE_REQUEST, function* ({
        transactionType, request, isRecreate,
    }, context) {
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(post, getServerUrl(transactionType), request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.TRANSACTION_CREATE_SUCCESS,
                transactionType,
                data,
                isRecreate,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_CREATE_ERROR,
                error,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

export function* createSuccess() {
    yield takeEvery(actions.TRANSACTION_CREATE_SUCCESS, function* ({ transactionType, data, isRecreate }) {
        if (isRecreate) {
            const { transaction } = data;
            yield message.success(
                <>
                    {t`The ${getMsgType(transactionType)} ${transaction.number} is created.`}
                    &nbsp;
                    <a
                        // href={`${getWebBaseUrl(transactionType)}/${transaction.id}`}
                        // target="_blank"
                        // rel="noreferrer"
                        onClick={() => {
                            history.push(`${getWebBaseUrl(transactionType)}/${transaction.id}`);
                        }}
                    >
                        {t`View`}
                    </a>
                </>,
            );
        }
        else {
            yield message.success(t`The ${getMsgType(transactionType)} is created.`);
        }
    });
}

export function* createError() {
    yield takeEvery(actions.TRANSACTION_CREATE_ERROR, function* ({ error }) {
        if (!error.canSuppress)
            yield message.error(error.message);
    });
}

// Read all
export function* readAllRequest() {
    yield takeEvery(actions.TRANSACTION_READ_ALL_REQUEST, function* ({transactionType, request}, context) {
        try {
            yield put({type: AppActions.APP_START_PULLING});
            const response = yield call(get, getServerUrl(transactionType), request, context);
            const data = yield response.data;

            const getBaseAmount = (amount, exchangeRate) => {
                if (!amount && amount !== 0)
                    return null;
                return new Big(amount)
                    .times(new Big(exchangeRate)).round(2).toNumber();
            };
            const preprocessForeign = transaction => {
                const {amount, balance, exchange_rate} = transaction;
                return {
                    ...transaction,
                    baseAmount: getBaseAmount(amount, exchange_rate),
                    baseBalance: getBaseAmount(balance, exchange_rate), // bal. & overpayment in home currency
                };
            };
            // OK
            yield put({
                type: actions.TRANSACTION_READ_ALL_SUCCESS,
                transactions: data.transactions.map(preprocessForeign),
                pagination: data.paging,
                transactionType,
                request,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_READ_ALL_ERROR,
                error,
            });
        }
        finally {
            yield put({type: AppActions.APP_STOP_PULLING});
        }
    });
}

export function* readAllSuccess() {
    yield takeEvery(actions.TRANSACTION_READ_ALL_SUCCESS, () => {});
}

export function* readAllError() {
    yield takeEvery(actions.TRANSACTION_READ_ALL_ERROR, function* ({error}) {
        console.log('aaa error', error);
        yield message.error(error.message);
    });
}

// Read
export function* readRequest() {
    yield takeEvery(actions.TRANSACTION_READ_REQUEST, function* ({ transactionType, id, request }, context) {
        try {
            yield put({type: AppActions.APP_START_PULLING});
            const response = yield call(get, `${getServerUrl(transactionType)}/${id}`, request, context);
            const data = yield response.data;

            if (transactionType === Type.stock_value) {
                yield put({
                    type: actions.TRANSACTION_READ_SUCCESS,
                    transaction: data,
                });
            }
            else {
                yield put({
                    type: actions.TRANSACTION_READ_SUCCESS,
                    transaction: data.transaction,
                });
            }
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_READ_ERROR,
                error,
            });
        }
        finally {
            yield put({type: AppActions.APP_STOP_PULLING});
        }
    });
}

export function* readSuccess() {
    yield takeEvery(actions.TRANSACTION_READ_SUCCESS, () => {});
}

export function* readError() {
    yield takeEvery(actions.TRANSACTION_READ_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

// Update
export function* updateRequest() {
    yield takeEvery(actions.TRANSACTION_UPDATE_REQUEST, function* (action, context) {
        const { transactionType, id, request } = action;
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            const response = yield call(puto, `${getServerUrl(transactionType)}/${id}`, request, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.TRANSACTION_UPDATE_SUCCESS,
                transactionType,
                transaction: transactionType === Type.stock_value ? data : data.transaction,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_UPDATE_ERROR,
                error,
                action,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

export function* updateSuccess() {
    yield takeEvery(actions.TRANSACTION_UPDATE_SUCCESS, function* ({ transactionType }) {
        yield message.success(t`The ${getMsgType(transactionType)} is updated.`);
    });
}

export function* updateError() {
    yield takeEvery(actions.TRANSACTION_UPDATE_ERROR, function* ({ error }) {
        if (!error.canSuppress)
            yield message.error(error.message);
    });
}

// Update status
export function* updateStatusRequest() {
    yield takeEvery(actions.TRANSACTION_UPDATE_STATUS_REQUEST, function* (action, context) {
        const { transactionType, id, request } = action;
        try {
            yield put({ type: AppActions.APP_START_PUSHING });

            context.allow_intercept = true;
            yield call(patch, `${getServerUrl(transactionType)}/${id}`, request, context);

            yield put({
                type: actions.TRANSACTION_UPDATE_STATUS_SUCCESS,
                request,
                transactionType,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_UPDATE_STATUS_ERROR,
                error,
                action,
            });
        }
        finally {
            yield put({ type: AppActions.APP_STOP_PUSHING });
        }
    });
}

export function* updateStatusSuccess() {
    yield takeEvery(actions.TRANSACTION_UPDATE_STATUS_SUCCESS, function* ({ transactionType, request }) {
        yield message.success(
            t`The ${getMsgType(transactionType)} is marked as ${getStatus(request.status)}.`,
        );
    });
}

export function* updateStatusError() {
    yield takeEvery(actions.TRANSACTION_UPDATE_STATUS_ERROR, function* ({ error }) {
        if (!error.canSuppress)
            yield message.error(error.message);
    });
}

// Delete
export function* deleteRequest() {
    yield takeEvery(actions.TRANSACTION_DELETE_REQUEST, function* (action, context) {
        const {transactionType, id, request} = action;
        try {
            yield put({type: AppActions.APP_START_PUSHING});

            context.allow_intercept = true;
            yield call(deleto, `${getServerUrl(transactionType)}/${id}`, request || {}, context);

            yield put({
                type: actions.TRANSACTION_DELETE_SUCCESS,
                transactionType,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_DELETE_ERROR,
                error,
                action,
            });
        }
        finally {
            yield put({type: AppActions.APP_STOP_PUSHING});
        }
    });
}

export function* deleteSuccess() {
    yield takeEvery(actions.TRANSACTION_DELETE_SUCCESS, function* ({transactionType}) {
        yield message.success(t`The ${getMsgType(transactionType)} is deleted.`);
    });
}

export function* deleteError() {
    yield takeEvery(actions.TRANSACTION_DELETE_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

// Read journal entries
export function* readJournalEntriesRequest() {
    yield takeEvery(actions.TRANSACTION_READ_JOURNAL_ENTRIES_REQUEST, function* (
        {transactionType, id}, context) {
        try {
            yield put({type: AppActions.APP_START_PULLING});
            const response = yield call(get,
                `${getServerUrl(transactionType)}/${id}/journal_entries`,
                null,
                context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.TRANSACTION_READ_JOURNAL_ENTRIES_SUCCESS,
                journalEntries: data.journal_entries,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_READ_JOURNAL_ENTRIES_ERROR,
                error,
            });
        }
        finally {
            yield put({type: AppActions.APP_STOP_PULLING});
        }
    });
}

export function* readJournalEntriesSuccess() {
    yield takeEvery(actions.TRANSACTION_READ_JOURNAL_ENTRIES_SUCCESS, () => {});
}

export function* readJournalEntriesError() {
    yield takeEvery(actions.TRANSACTION_READ_JOURNAL_ENTRIES_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

// Read journal entries
export function* readProfitabilityRequest() {
    yield takeEvery(actions.TRANSACTION_READ_PROFITABILITY_REQUEST, function* ({ transactionType, id},
        context) {
        try {
            yield put({type: AppActions.APP_START_PULLING});
            const response = yield call(get, `${getServerUrl(transactionType)}/${id}/profits`, null, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.TRANSACTION_READ_PROFITABILITY_SUCCESS,
                profitability: data.transaction,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_READ_PROFITABILITY_ERROR,
                error,
            });
            yield message.error(error.message);
        }
        finally {
            yield put({type: AppActions.APP_STOP_PULLING});
        }
    });
}

// Read outstanding
export function* readOutstandingRequest() {
    yield takeEvery(
        actions.TRANSACTION_READ_OUTSTANDING_REQUEST,
        function* ({transactionType, request, withTableName}, context) {
            try {
                yield put({type: AppActions.APP_START_PULLING});
                const response = yield call(get, getOutstandingUrl(transactionType), request, context);
                const data = yield response.data;

                const linkItems = data.transactions.map(item => {
                    const balanceBig = new Big(item.balance || 0);
                    const applyBig = new Big(item.apply_amount || 0);
                    const balanceAfterApplyBig = balanceBig.minus(applyBig);

                    return ({
                        ...item,
                        balance_after_apply: balanceAfterApplyBig.toNumber(),
                    });
                });

                yield put({
                    type: actions.TRANSACTION_READ_OUTSTANDING_SUCCESS,
                    linkItems,
                    withTableName,
                    transactionType,
                });
            }
            catch (error) {
                yield put({
                    type: actions.TRANSACTION_READ_OUTSTANDING_ERROR,
                    error,
                });
            }
            finally {
                yield put({type: AppActions.APP_STOP_PULLING});
            }
        });
}

export function* readOutstandingSuccess() {
    yield takeEvery(actions.TRANSACTION_READ_OUTSTANDING_SUCCESS, () => {});
}

export function* readOutstandingError() {
    yield takeEvery(actions.TRANSACTION_READ_OUTSTANDING_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

// Read Pending Invoices
export function* readPendingInvoicesRequest() {
    yield takeEvery(actions.TRANSACTION_READ_PENDING_INVOICE_REQUEST, function* ({request}, context) {
        try {
            yield put({type: AppActions.APP_START_PULLING});
            const response = yield call(get, '/taxes/pending_invoices', request, context);
            const data = yield response.data;

            yield put({
                type: actions.TRANSACTION_READ_PENDING_INVOICE_SUCCESS,
                linkItems: data,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_READ_PENDING_INVOICE_ERROR,
                error,
            });
        }
        finally {
            yield put({type: AppActions.APP_STOP_PULLING});
        }
    });
}

export function* readPendingInvoicesSuccess() {
    yield takeEvery(actions.TRANSACTION_READ_PENDING_INVOICE_SUCCESS, () => {});
}

export function* readPendingInvoicesError() {
    yield takeEvery(actions.TRANSACTION_READ_PENDING_INVOICE_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

// Send email
export function* sendEmailRequest() {
    yield takeEvery(actions.TRANSACTION_SEND_EMAIL_REQUEST, function* ({
        transactionType, id, request,
    }, context) {
        try {
            yield put({type: AppActions.APP_START_PULLING});
            yield call(post, `${getServerUrl(transactionType)}/${id}/email`, request, context);

            yield put({
                type: actions.TRANSACTION_SEND_EMAIL_SUCCESS,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_SEND_EMAIL_ERROR,
                error,
            });
        }
        finally {
            yield put({type: AppActions.APP_STOP_PULLING});
        }
    });
}

export function* sendEmailSuccess() {
    yield takeEvery(actions.TRANSACTION_SEND_EMAIL_SUCCESS, function* () {
        yield message.success(t`The email is sent.`);
    });
}

export function* sendEmailError() {
    yield takeEvery(actions.TRANSACTION_SEND_EMAIL_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

// Batch actions: View Merged PDF
export function* viewBulkPdfRequest() {
    yield takeEvery(
        actions.TRANSACTION_BULK_VIEW_PDF_REQUEST,
        function* ({transactionType, request}, context) {
            const msgKey = Math.random().toString();
            const msgConfig = {
                key: msgKey,
                type: "loading",
                content: t`Generating PDFs...`,
                duration: 0,
            // className: "",
            };

            try {
                yield put({type: AppActions.APP_START_PUSHING});

                message.open(msgConfig); // Show a message that the PDFs are loading

                const response = yield call(post, `${getServerUrl(transactionType)}/pdf`, request, context);
                const data = yield response.data;

                message.destroy(msgKey); // close the message when pdf loaded
                // window.open(data.url); // open the merged pdfs in a new tab
                windowOpenPopUpFallback(
                    data.url,
                    t`Transaction PDFs Generated`,
                    <Trans>Your transaction PDFs has been generated. Click OK to view/download.</Trans>,
                );

                // OK
                yield put({
                    type: actions.TRANSACTION_BULK_VIEW_PDF_SUCCESS,
                    data,
                    msgKey,
                });
            }
            catch (error) {
                yield put({
                    type: actions.TRANSACTION_BULK_VIEW_PDF_ERROR,
                    error,
                    msgKey,
                });
            }
            finally {
                yield put({type: AppActions.APP_STOP_PUSHING});
            }
        });
}

export function* viewBulkPdfSuccess() {
    yield takeEvery(actions.TRANSACTION_BULK_VIEW_PDF_SUCCESS, () => {});
}

export function* viewBulkPdfError() {
    yield takeEvery(actions.TRANSACTION_BULK_VIEW_PDF_ERROR, function* ({error, msgKey}) {
        message.destroy(msgKey); // close the generating pdf message when error has been encountered
        if (!error.canSuppress)
            yield message.error(t`Fail to generate PDFs: ${error.message}`);
    });
}

// Send email
export function* sendBulkEmailRequest() {
    yield takeEvery(
        actions.TRANSACTION_SEND_BULK_EMAIL_REQUEST,
        function* ({ transactionType, request}, context) {
            try {
                yield put({type: AppActions.APP_START_PULLING});
                yield call(post, `${getServerUrl(transactionType)}/email`, request, context);
                yield put({
                    type: actions.TRANSACTION_SEND_BULK_EMAIL_SUCCESS,
                });
            }
            catch (error) {
                yield put({
                    type: actions.TRANSACTION_SEND_BULK_EMAIL_ERROR,
                    error,
                });
            }
            finally {
                yield put({type: AppActions.APP_STOP_PULLING});
            }
        });
}

export function* sendBulkEmailSuccess() {
    yield takeEvery(actions.TRANSACTION_SEND_BULK_EMAIL_SUCCESS, function* () {
        yield message.success(t`The email is sent.`);
    });
}

export function* sendBulkEmailError() {
    yield takeEvery(actions.TRANSACTION_SEND_BULK_EMAIL_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

function getBulkVoidDeleteSuccessMsg(transactionType, success_count, fail_count, mode = 'void') {
    // Message Logics
    const successText = mode === 'void' ? `successfully voided` : `successfully deleted`;
    const successMsg = plural(
        success_count, {
            one: `# ${getMsgType(transactionType)} ${successText}`,
            other: `# ${getMsgTypePluralized(transactionType)} ${successText}`,
        },
    );

    const skippedMsg = plural(
        fail_count, {
            one: `# ${getMsgType(transactionType)} skipped`,
            other: `# ${getMsgTypePluralized(transactionType)} skipped`,
        },
    );

    if (success_count > 0 && fail_count > 0)
        return t`${successMsg}, ${skippedMsg}.`;
    if (success_count > 0)
        return t`${successMsg}.`;
    return t`${skippedMsg}.`;
}

function getBulkReadySuccessMsg(transactionType, success_count, fail_count) {
    // Message Logics
    const successMsg = plural(
        success_count, {
            one: `# ${getMsgType(transactionType)} successfully set as ready`,
            other: `# ${getMsgTypePluralized(transactionType)} successfully  set as ready`,
        },
    );

    const skippedMsg = plural(
        fail_count, {
            one: `# ${getMsgType(transactionType)} skipped`,
            other: `# ${getMsgTypePluralized(transactionType)} skipped`,
        },
    );

    if (success_count > 0 && fail_count > 0)
        return t`${successMsg}, ${skippedMsg}.`;
    if (success_count > 0)
        return t`${successMsg}.`;
    return t`${skippedMsg}.`;
}

function getBulkPendingApprovalSuccessMsg(transactionType, success_count, fail_count) {
    // Message Logics
    const successMsg = plural(
        success_count, {
            one: `# ${getMsgType(transactionType)} successfully set as Pending Approval`,
            other: `# ${getMsgTypePluralized(transactionType)} successfully  set as Pending Approval`,
        },
    );

    const skippedMsg = plural(
        fail_count, {
            one: `# ${getMsgType(transactionType)} skipped`,
            other: `# ${getMsgTypePluralized(transactionType)} skipped`,
        },
    );

    if (success_count > 0 && fail_count > 0)
        return t`${successMsg}, ${skippedMsg}.`;
    if (success_count > 0)
        return t`${successMsg}.`;
    return t`${skippedMsg}.`;
}

function* bulkUpdateStatusRequest() {
    yield takeEvery(
        [
            actions.TRANSACTION_BULK_PENDING_APPROVAL_REQUEST,
            actions.TRANSACTION_BULK_READY_REQUEST,
            actions.TRANSACTION_BULK_VOID_REQUEST,
            actions.TRANSACTION_BULK_UNVOID_REQUEST,
        ],
        function* ({ transactionType, request, status, type }, context) {
            try {
                yield put({type: AppActions.APP_START_PULLING});

                if (status === 'ready')
                    context.allow_intercept = true;

                const response = yield call(
                    post,
                    `${getServerUrl(transactionType)}/batch_status`,
                    {...request, action: status },
                    context,
                );

                const { success_count, fail_count } = yield response.data;

                let msg;
                switch (status) {
                    case 'ready':
                        msg = getBulkReadySuccessMsg(transactionType, success_count, fail_count);
                        break;
                    case 'pending_approval':
                        msg = getBulkPendingApprovalSuccessMsg(transactionType, success_count, fail_count);
                        break;
                    case 'void':
                        msg = getBulkVoidDeleteSuccessMsg(transactionType, success_count, fail_count);
                        break;
                    default:
                        break;
                }

                message.success(msg);

                const { lastReadAllRequest } = yield select(state => state.Transaction);
                yield put(
                    actions.transactionReadAll(transactionType, lastReadAllRequest),
                );
                yield take([actions.TRANSACTION_READ_ALL_SUCCESS, actions.TRANSACTION_READ_ALL_ERROR]);
                yield put({
                    type: type.replace('_REQUEST', '_SUCCESS'),
                });
            }
            catch (error) {
                yield put({
                    type: type.replace('_REQUEST', '_ERROR'),
                    error,
                });
                if (!error.has_intercepted)
                    yield message.error(error.message);
            }
            finally {
                yield put({type: AppActions.APP_STOP_PULLING});
            }
        });
}

function* bulkDeleteRequest() {
    yield takeEvery(
        actions.TRANSACTION_BULK_DELETE_REQUEST,
        function* ({ transactionType, request}, context) {
            try {
                yield put({type: AppActions.APP_START_PULLING});

                const response = yield call(
                    post, `${getServerUrl(transactionType)}/delete`, request, context,
                );
                const { success_count, fail_count } = yield response.data;

                message.success(
                    getBulkVoidDeleteSuccessMsg(transactionType, success_count, fail_count, 'delete'),
                );

                const { lastReadAllRequest } = yield select(state => state.Transaction);
                yield put(
                    actions.transactionReadAll(transactionType, lastReadAllRequest),
                );
                yield take([actions.TRANSACTION_READ_ALL_SUCCESS, actions.TRANSACTION_READ_ALL_ERROR]);
                yield put({
                    type: actions.TRANSACTION_BULK_DELETE_SUCCESS,
                });
            }
            catch (error) {
                yield put({
                    type: actions.TRANSACTION_BULK_DELETE_ERROR,
                    error,
                });
                yield message.error(error.message);
            }
            finally {
                yield put({type: AppActions.APP_STOP_PULLING});
            }
        });
}

export function* getTransferOptionRequest() {
    yield takeEvery(actions.TRANSACTION_TRANSFER_READ_ALL_IDS_REQUEST, function* (action, context) {
        try {
            const state = yield select();
            const {
                originType,
                selectedIds,
                options,
                readCount,
            } = state.Transaction.transfer;

            // Stage 1: Filter out data
            for (const id of selectedIds) {
                // If not existed
                if (!options.find(d => d.id === id)) {
                    const res = yield call(get, `${getServerUrl(originType)}/${id}`, null, context);
                    const d = yield res.data.transaction;

                    // Remove subtotal & subtitle
                    d.form_items = d.form_items.filter(i => i.type !== 'subtotal' && i.type !== 'subtitle');

                    const { originalQuantities } = state.Transaction.transfer;

                    // Compute original quantities
                    d.form_items = d.form_items.map(item => {
                        const newItem = {...item};
                        const originalQtyItem = originalQuantities.find(i => (
                            i.transaction_id === id && i.item_id === newItem.id
                        ));
                        if (originalQtyItem && !newItem.original_quantity)
                            newItem.original_quantity = originalQtyItem.quantity;
                        return newItem;
                    });

                    // Remove item with zero balance
                    d.form_items = d.form_items.filter(item => (
                        item.balance_quantity + (item.original_quantity || 0)
                    ));

                    // Append to <data>
                    options.push(d);
                }
            }

            yield put({
                type: actions.TRANSACTION_TRANSFER_READ_ALL_IDS_SUCCESS,
                options,
                readCount: readCount + 1,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_TRANSFER_READ_ALL_IDS_ERROR,
                error,
            });
        }
        finally {
            // yield put({type: AppActions.APP_STOP_PULLING});
        }
    });
}

export function* getTransferOptionError() {
    yield takeEvery(actions.TRANSACTION_TRANSFER_READ_ALL_IDS_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

// Read audit logs
export function* readAllAuditsRequest() {
    yield takeEvery(actions.TRANSACTION_READ_AUDITS_REQUEST, function* ({id, pageQueryString}, context) {
        try {
            yield put({type: AppActions.APP_START_PULLING});
            const response = yield call(get, `/transactions/${id}/actions`, pageQueryString, context);
            const data = yield response.data;

            // OK
            yield put({
                type: actions.TRANSACTION_READ_AUDITS_SUCCESS,
                auditLogs: data.records,
                auditLogsPagination: data.paging,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_READ_AUDITS_ERROR,
                error,
            });
        }
        finally {
            yield put({type: AppActions.APP_STOP_PULLING});
        }
    });
}

export function* readAllAuditsSuccess() {
    yield takeEvery(actions.TRANSACTION_READ_AUDITS_SUCCESS, () => {});
}

export function* readAllAuditsError() {
    yield takeEvery(actions.TRANSACTION_READ_AUDITS_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

// Create interest invoices
export function* createInterestInvoicesRequest() {
    yield takeEvery(actions.TRANSACTION_CREATE_INTEREST_INVOICES_REQUEST, function* ({requestData}, context) {
        try {
            yield put({type: AppActions.APP_START_PUSHING});
            const response = yield call(
                post, '/sales/invoices/interest_invoices/batch', requestData, context,
            );
            const data = yield response.data;

            // OK
            yield put({
                type: actions.TRANSACTION_CREATE_INTEREST_INVOICES_SUCCESS,
                count: data.count,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_CREATE_INTEREST_INVOICES_ERROR,
                error,
            });
        }
        finally {
            yield put({type: AppActions.APP_STOP_PUSHING});
        }
    });
}

export function* createInterestInvoicesSuccess() {
    yield takeEvery(actions.TRANSACTION_CREATE_INTEREST_INVOICES_SUCCESS, function* ({ count }) {
        yield notification.open({
            message: t`Interest Invoices`,
            description: t`${count} late payment interest invoices created.`,
            duration: 0,
        });
    });
}

export function* createInterestInvoicesError() {
    yield takeEvery(actions.TRANSACTION_CREATE_INTEREST_INVOICES_ERROR, function* ({error}) {
        yield message.error(error.message);
    });
}

export function* openEmailTrackerRequest() {
    yield takeEvery(
        actions.TRANSACTION_OPEN_EMAIL_TRACKER_REQUEST,
        function* ({ transactionType, id}, context) {
            try {
                const url =
                    transactionType === 'contacts' ?
                        `/contacts/${id}/email_records` :
                        `${getServerUrl(transactionType)}/${id}/email_records`;

                const response = yield call(get, url, {}, context);
                const data = yield response.data;

                // OK
                yield put({
                    type: actions.TRANSACTION_OPEN_EMAIL_TRACKER_SUCCESS,
                    records: data.records,
                    transactionType,
                });
            }
            catch (error) {
                yield put({
                    type: actions.TRANSACTION_OPEN_EMAIL_TRACKER_ERROR,
                    error,
                });
            }
        });
}

function* readTransferToTransactions() {
    yield takeLatest(actions.TRANSACTION_READ_TRANSFER_TO_TRANSACTIONS_REQUEST, function* (action, context) {
        try {
            const transaction = yield select(state => state.Transaction.transaction);
            const url = `/transactions/${transaction.id}/items/${action.itemId}/transfer_status`;
            const response = yield call(get, url, null, context);
            const data = yield response.data;

            yield put({
                type: actions.TRANSACTION_READ_TRANSFER_TO_TRANSACTIONS_SUCCESS,
                transactions: data.transactions,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_READ_TRANSFER_TO_TRANSACTIONS_ERROR,
                error,
            });
        }
    });
}

export function* getPdfPreviewRequest() {
    yield takeEvery(actions.TRANSACTION_GET_PDF_PREVIEW_REQUEST, function* (action, context) {
        const { transactionType, request } = action;

        try {
            context.isPdfByte = true;
            const response = yield call(
                post, `${getServerUrl(transactionType)}/preview_pdf`, request, context,
            );
            const { blobUrl } = yield response.data;

            yield put({
                type: actions.TRANSACTION_GET_PDF_PREVIEW_SUCCESS,
                blobUrl,
            });
        }
        catch (error) {
            yield put({
                type: actions.TRANSACTION_GET_PDF_PREVIEW_ERROR,
                error,
            });
            yield message.error(error.message);
        }
        return null;
    });
}

export default function* rootSaga() {
    yield all([
        fork(createRequest),
        fork(createSuccess),
        fork(createError),
        fork(readAllRequest),
        fork(readAllSuccess),
        fork(readAllError),
        fork(readRequest),
        fork(readSuccess),
        fork(readError),
        fork(updateRequest),
        fork(updateSuccess),
        fork(updateError),
        fork(updateStatusRequest),
        fork(updateStatusSuccess),
        fork(updateStatusError),
        fork(deleteRequest),
        fork(deleteSuccess),
        fork(deleteError),
        fork(readJournalEntriesRequest),
        fork(readJournalEntriesSuccess),
        fork(readJournalEntriesError),
        fork(readProfitabilityRequest),
        fork(readOutstandingRequest),
        fork(readOutstandingSuccess),
        fork(readOutstandingError),
        fork(readPendingInvoicesRequest),
        fork(readPendingInvoicesSuccess),
        fork(readPendingInvoicesError),
        fork(sendEmailRequest),
        fork(sendEmailSuccess),
        fork(sendEmailError),
        fork(viewBulkPdfRequest),
        fork(viewBulkPdfSuccess),
        fork(viewBulkPdfError),
        fork(sendBulkEmailRequest),
        fork(sendBulkEmailSuccess),
        fork(sendBulkEmailError),
        fork(bulkUpdateStatusRequest),
        fork(bulkDeleteRequest),
        fork(getTransferOptionRequest),
        fork(getTransferOptionError),
        fork(readAllAuditsRequest),
        fork(readAllAuditsSuccess),
        fork(readAllAuditsError),
        fork(createInterestInvoicesRequest),
        fork(createInterestInvoicesSuccess),
        fork(createInterestInvoicesError),
        fork(openEmailTrackerRequest),
        fork(readTransferToTransactions),
        fork(getPdfPreviewRequest),
        fork(initRequest),
        // ...TransactionCalculationSagas,
    ]);
}
